import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle } from 'lucide-react';
import { BrandLogo } from '../components/brand/BrandLogo';
import { getAttribution } from '../lib/attribution';
import { safeCapture } from '../lib/analytics';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    businessName: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.password.length < 6) {
      setError('Le mot de passe doit faire au moins 6 caractères.');
      setLoading(false);
      return;
    }

    const result = await register(formData.email, formData.password, formData.name, formData.businessName);
    setLoading(false);

    if (result.success) {
      const acq = getAttribution();
      safeCapture('merchant_signed_up', { source: acq?.source || 'direct', campaign: acq?.campaign, landing: acq?.landing });
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <BrandLogo size="lg" />
            </Link>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Essai gratuit</h1>
            <p className="text-slate-600 mb-6">14 jours gratuits. Aucune carte bancaire requise.</p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="register-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Votre nom</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Jean Dupont"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessName">Nom du commerce</Label>
                <Input
                  id="businessName"
                  name="businessName"
                  type="text"
                  placeholder="Mon Café"
                  value={formData.businessName}
                  onChange={handleChange}
                  data-testid="register-business-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <p className="text-xs text-slate-500">6 caractères minimum</p>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0B0D12] hover:bg-black"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Créer mon compte
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-600">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-[#0B0D12] underline underline-offset-4 hover:text-black font-medium">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Benefits (hidden on mobile) */}
      <div className="hidden lg:flex flex-1 bg-[#0B0D12] items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="text-3xl font-bold mb-8">
            Votre carte de fidélité prête en 5 minutes
          </h2>
          <ul className="space-y-4">
            {[
              'À vos couleurs, dans Apple Wallet et Google Wallet',
              'Vos clients l’ajoutent sans application',
              'Vous tamponnez avec l’appareil photo de votre téléphone',
              'Vos offres sur l’écran verrouillé de leur iPhone',
              'Vous voyez qui revient, sur les 30 derniers jours',
            ].map((benefit) => (
              <li key={benefit} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-white/60 flex-shrink-0" />
                <span className="text-white/80">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
